import { useCallback, useEffect, useRef, useState } from 'react';
import { PopupType, ScanPromptInfo, ScanResultEnvelope } from './types';

/**
 * `useScanResultPrompt` - PDA 扫描结果弹窗状态机。
 *
 * 职责：
 *   - 持有当前展示的 `prompt`（4 种 popupType 之一或 null）
 *   - 同时记录引发本次弹窗的 `barCode`（运单号 / 包牌号），弹窗体里渲染末 6 位
 *   - 按优先级裁决「新事件是否能顶掉旧弹窗」
 *   - 给 SUCCESS / NOTICE 类型挂 1.5s 自动消失计时器；INTERCEPT / NEED_ACTION 必须手动关闭或被下次扫描清掉
 *
 * 优先级规则（PDF：拦截 > 需处理 > 成功 > 临时）：
 *   - 当前是 INTERCEPT / NEED_ACTION 时，新 SUCCESS / NOTICE 被静默忽略（视觉不变）
 *   - 否则新事件替换旧事件并重启计时器（如适用）
 *
 * 「下一次扫描发生」由调用方在扫描入口主动调 `clearPrompt()`，hook 内不感知扫描事件。
 */

const AUTO_DISMISS_MS = 1500;
const SUCCESS_WITH_BORDER_DISMISS_MS = 4000;


/** 从 envelope 里提取 promptInfo：优先读 data.promptInfo（已嵌入），再降级到 success/message 推断。 */
const toPrompt = (envelope: ScanResultEnvelope): ScanPromptInfo => {
    const embedded = (envelope.data as { promptInfo?: ScanPromptInfo | null } | null | undefined)?.promptInfo;
    if (embedded) return embedded;
    // 系统级异常兜底：data=null，message="Internal server error"
    return {
        messageKey: envelope.success ? 'SYSTEM_SUCCESS' : 'SYSTEM_ERROR',
        popupType: envelope.success ? PopupType.SUCCESS : PopupType.INTERCEPT,
        content: envelope.message ?? '',
    };
};

export interface UseScanResultPromptResult {
    /** 当前展示的弹窗信息；null 表示无弹窗 */
    prompt: ScanPromptInfo | null;
    /** 引发当前弹窗的 barCode（运单号 / 包牌号），用于弹窗里渲染末 6 位；null 表示无 */
    barCode: string | null;
    /** 收到后端响应后调用：自动按优先级 + 计时器处理 */
    showResult: (envelope: ScanResultEnvelope, barCode?: string) => void;
    /** 前端"快速重复扫描"时调用：黑底 NOTICE，1.5s 自动消失 */
    showNotice: (content: string, barCode?: string, messageKey?: string) => void;
    /** 新一次扫描发起 / 用户手动关闭弹窗时调用 */
    clearPrompt: () => void;
}

export function useScanResultPrompt(): UseScanResultPromptResult {
    const [prompt, setPrompt] = useState<ScanPromptInfo | null>(null);
    const [barCode, setBarCode] = useState<string | null>(null);
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const promptRef = useRef<ScanPromptInfo | null>(null);
    promptRef.current = prompt;

    const cancelTimer = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    const clearPrompt = useCallback(() => {
        cancelTimer();
        setPrompt(null);
        setBarCode(null);
    }, [cancelTimer]);

    const setPromptWithTimer = useCallback((next: ScanPromptInfo, nextBarCode: string | null) => {
        cancelTimer();
        setPrompt(next);
        setBarCode(nextBarCode);
        if (next.popupType === PopupType.SUCCESS || next.popupType === PopupType.NOTICE || next.popupType === PopupType.SUCCESS_WITH_BORDER) {
            const dismissMs = next.popupType === PopupType.SUCCESS_WITH_BORDER ? SUCCESS_WITH_BORDER_DISMISS_MS : AUTO_DISMISS_MS;
            timerRef.current = setTimeout(() => {
                timerRef.current = null;
                setPrompt(curr => (curr === next ? null : curr));
                setBarCode(curr => (curr === nextBarCode ? null : curr));
            }, dismissMs);
        }
    }, [cancelTimer]);

    const showResult = useCallback((envelope: ScanResultEnvelope, nextBarCode?: string) => {
        const next = toPrompt(envelope);
        // 每次服务端扫描结果直接替换当前弹窗（含高优先级）：
        // PRD 规则：拦截/需处理弹窗在「下一次扫描发生时」消失，与新结果级别无关。
        // 优先级裁决只用于 showNotice（重复快速扫描检测，非新服务端结果）。
        setPromptWithTimer(next, nextBarCode ?? null);
    }, [setPromptWithTimer]);

    const showNotice = useCallback(
        (content: string, nextBarCode?: string, messageKey: string = 'DUPLICATE_SCAN') => {
            const next: ScanPromptInfo = { messageKey, popupType: PopupType.NOTICE, content };
            setPromptWithTimer(next, nextBarCode ?? null);
        },
        [setPromptWithTimer],
    );

    // 组件卸载时清掉计时器，防止内存泄漏
    useEffect(() => () => cancelTimer(), [cancelTimer]);

    return { prompt, barCode, showResult, showNotice, clearPrompt };
}
